import React from "react";
import { Instagram, Quote } from "lucide-react";

interface TestimonialCardProps {
  name: string;
  image: string;
  quote: string;
  instagramHandle: string;
  instagramUrl: string;
}

const TestimonialCard = ({
  name,
  image,
  quote,
  instagramHandle,
  instagramUrl,
}: TestimonialCardProps) => {
  return (
    <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 via-ophira-dark/70 to-black/80 p-8 md:p-10 shadow-[0_20px_80px_rgba(0,0,0,0.35)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_25px_90px_rgba(0,0,0,0.45)]">
      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute -left-10 top-6 h-36 w-36 rounded-full bg-ophira-yellow/20 blur-3xl" />
        <div className="absolute -right-14 bottom-4 h-44 w-44 rounded-full bg-ophira-purple/25 blur-3xl" />
      </div>

      <div className="relative flex flex-col gap-6">
        <div className="flex items-center gap-4 md:gap-6">
          <div className="relative h-16 w-16 md:h-20 md:w-20 overflow-hidden rounded-full ring-4 ring-white/10 shadow-xl">
            <img
              src={image}
              alt={name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>

          <div className="flex flex-col gap-1">
            <div className="text-xs uppercase tracking-[0.25em] text-ophira-yellow">
              Artist
            </div>
            <h4 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
              <span> {name}</span>
            </h4>
            <a
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-ophira-yellow/80 hover:text-ophira-yellow transition-colors"
            >
              <Instagram className="h-4 w-4" />
              <span>{instagramHandle}</span>
            </a>
          </div>
        </div>

        <div className="flex gap-4">
          <Quote className="h-8 w-8 text-ophira-yellow/80" />
          <blockquote className="text-lg md:text-xl leading-relaxed text-white/90">
            {quote}
          </blockquote>
        </div>
      </div>
    </div>
  );
};

const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "UTKARSH SAXENA",
      image: "/uploads/utkarsh-saxena-artist.webp",
      quote:
        "The website is very handy and simple to navigate, making it easy to distribute my music globally without any technical issues. Overall, their support is top-class.",
      instagramHandle: "@utkarshsaxenaofficial",
      instagramUrl: "https://www.instagram.com/utkarshsaxenaofficial/",
    },
    {
      name: "SHRESTH DIXIT",
      image: "/uploads/shresth-dixit-artist.webp",
      quote:
        "Unlike other distributors I’ve used, YouAux usually replies within 24 hours and has always helped me with my distribution questions, which has also helped boost my audience.",
      instagramHandle: "@shresthdixitofficial",
      instagramUrl: "https://www.instagram.com/shresthdixitofficial/",
    },
  ];

  return (
    <section className="py-2 md:py-20 bg-black">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-ophira-yellow/30 to-transparent mb-14 md:mb-24" />
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-start justify-between gap-16 mb-12">
          <div className="w-full lg:w-1/3">
            <div className="ophira-section-label">TESTIMONIALS</div>
          </div>

          <div className="w-full lg:w-2/3">
            <h2 className="ophira-section-title">What Our Artists Say</h2>

            <p className="text-white/65 mb-10 max-w-xl">
              Hear directly from the artists who have used YouAux to distribute
              their music worldwide.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} {...testimonial} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
